import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { Form } from 'react-final-form';
import api from '../common/api';
import { TextArea } from '../form/TextArea';
import { formatDate } from '../format/DateTime';
import { useFacilityNotes } from '../hooks/FacilityNote';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { useServerTableFilterState } from '../table/useServerTableFilterState';
import { useAlerts } from '../alert/AlertContext';
import { Facility } from '@reliance/types/models/Facility';

const COLUMNS = [
  { id: 'dateEntered', meta: { filterComponent: 'DateRangeFilter' } },
  { id: 'userName' },
  { id: 'note' },
];

type FacilityNotesTabProps = {
  facility: Facility;
};

const FacilityNotesTab: React.FC<FacilityNotesTabProps> = ({ facility }) => {
  const alerts = useAlerts();
  const baseRecord = useMemo(
    () => ({ facilityId: facility.id }) as Record<string, unknown>,
    [facility.id]
  );
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting: [{ id: 'dateEntered', desc: true }],
  });
  const filters = useMemo(
    () => ({ ...tableState.mergedRecord, facilityId: facility.id }),
    [tableState.mergedRecord, facility.id]
  );
  const facilityNotes = useFacilityNotes(filters);

  useNotifyTabLoading('notes', facilityNotes.loading);

  const columnHelper = createColumnHelper<Record<string, unknown>>();
  const columns = [
    columnHelper.accessor('dateEntered.date', {
      cell: (data) => formatDate(data.getValue() as string),
      header: () => 'Date',
      id: 'dateEntered',
      filterFn: 'dateRange',
      meta: {
        filterComponent: 'DateRangeFilter',
        startDate: moment().startOf('day').subtract(20, 'year'),
      },
      size: 135,
    }),
    columnHelper.accessor('userName', {
      header: 'User',
      size: 150,
    }),
    columnHelper.accessor('note', {
      header: 'Note',
    }),
  ];

  const onSubmit = async (values: { note?: string }) => {
    try {
      await api.post('/facility-note', {
        note: {
          entityId: facility.id,
          note: values.note,
        },
      });
      alerts.success('The note was successfully added');
      tableState.setPagination({ pageIndex: 0, pageSize: 100 });
      tableState.setSorting([{ id: 'dateEntered', desc: true }]);
      tableState.setColumnFilters([]);
      facilityNotes.execute();
    } catch {
      alerts.error('An error occurred while adding the note');
    }
  };

  return (
    <Card>
      <Card.Body>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} className="mb-4">
              <div className="mb-3">
                <TextArea name="note" label="New Note" rows={3} />
              </div>
              <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  Add Note
                </button>
              </div>
            </form>
          )}
        />

        <BaseHtmlTable
          loading={facilityNotes.loading}
          rows={facilityNotes.result?.data || []}
          columnDefs={columns}
          perPage={100}
          paginationMode="server"
          page={facilityNotes.result?.page ?? tableState.pagination.pageIndex + 1}
          totalPages={facilityNotes.result?.totalPages}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
        />
      </Card.Body>
    </Card>
  );
};

export default FacilityNotesTab;
